import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  Chip,
} from "@mui/material";
import { 
  Save as SaveIcon, 
  Refresh, 
  Warning, 
  PhoneAndroid,
  Update,
  Logout,
  CheckCircle,
  Cancel
} from "@mui/icons-material";
import { api_urls } from "../../utils/api-urls/index.js";

const AppConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Form states
  const [minimumVersion, setMinimumVersion] = useState("");
  const [latestVersion, setLatestVersion] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  const [appStoreUrl, setAppStoreUrl] = useState("");
  const [playStoreUrl, setPlayStoreUrl] = useState("");
  
  // Force logout states
  const [forceLogoutDialogOpen, setForceLogoutDialogOpen] = useState(false);
  const [forceLogoutReason, setForceLogoutReason] = useState("");
  const [triggeringLogout, setTriggeringLogout] = useState(false);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(api_urls + "api/admin/app-config/admin");
      if (response.data && response.data.success) {
        const cfg = response.data.config;
        setConfig(cfg);
        setMinimumVersion(cfg.minimumVersion || "");
        setLatestVersion(cfg.latestVersion || "");
        setForceUpdate(cfg.forceUpdate || false);
        setMaintenanceMode(cfg.maintenanceMode || false);
        setMaintenanceMessage(cfg.maintenanceMessage || "");
        setUpdateMessage(cfg.updateMessage || "");
        setAppStoreUrl(cfg.appStoreUrl || "");
        setPlayStoreUrl(cfg.playStoreUrl || "");
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
      setSnackbar({ open: true, message: "Failed to fetch config", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      const payload = {
        minimumVersion,
        latestVersion,
        forceUpdate,
        maintenanceMode,
        maintenanceMessage,
        updateMessage,
        appStoreUrl,
        playStoreUrl,
      };
      const response = await axios.post(api_urls + "api/admin/app-config/update", payload);
      if (response.data && response.data.success) {
        setSnackbar({ open: true, message: "Configuration saved successfully!", severity: "success" });
        fetchConfig();
      } else {
        setSnackbar({ open: true, message: response.data.message || "Failed to save", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to save configuration", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerForceLogout = async () => {
    if (!forceLogoutReason) {
      setSnackbar({ open: true, message: "Please enter a reason for force logout", severity: "error" });
      return;
    }
    try {
      setTriggeringLogout(true);
      const response = await axios.post(api_urls + "api/admin/app-config/force-logout", {
        message: forceLogoutReason
      });
      if (response.data && response.data.success) {
        setSnackbar({ open: true, message: "Force logout triggered for all users!", severity: "success" });
        setForceLogoutDialogOpen(false);
        setForceLogoutReason("");
        fetchConfig();
      } else {
        setSnackbar({ open: true, message: response.data.message || "Failed", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to trigger force logout", severity: "error" });
    } finally {
      setTriggeringLogout(false);
    }
  };

  const handleClearForceLogout = async () => {
    if (!window.confirm("Are you sure you want to clear force logout? Users will no longer be forced to re-login.")) return;
    try {
      const response = await axios.post(api_urls + "api/admin/app-config/clear-force-logout");
      if (response.data && response.data.success) {
        setSnackbar({ open: true, message: "Force logout cleared!", severity: "success" });
        fetchConfig();
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to clear force logout", severity: "error" });
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">⚙️ App Configuration</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchConfig}>
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Version Management */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Update color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Version Management</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Minimum Required Version"
                value={minimumVersion}
                onChange={(e) => setMinimumVersion(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="2.13"
                helperText="Users with versions below this will be forced to update"
              />
              
              <TextField
                fullWidth
                label="Latest Version"
                value={latestVersion}
                onChange={(e) => setLatestVersion(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="2.14"
                helperText="Current latest version available"
              />
              
              <FormControlLabel
                control={
                  <Switch 
                    checked={forceUpdate} 
                    onChange={(e) => setForceUpdate(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography>Force Update</Typography>
                    <Typography variant="caption" color="textSecondary">
                      When enabled, users MUST update before using app
                    </Typography>
                  </Box>
                }
              />
              
              <TextField
                fullWidth
                label="Update Message"
                value={updateMessage}
                onChange={(e) => setUpdateMessage(e.target.value)}
                sx={{ mt: 2 }}
                multiline
                rows={2}
                placeholder="Please update to the latest version for new features and bug fixes."
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Store URLs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PhoneAndroid color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Store URLs</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Play Store URL"
                value={playStoreUrl}
                onChange={(e) => setPlayStoreUrl(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="https://play.google.com/store/apps/details?id=com.ksbm.bharatdarshanApplication"
              />
              
              <TextField
                fullWidth
                label="App Store URL (iOS)"
                value={appStoreUrl}
                onChange={(e) => setAppStoreUrl(e.target.value)}
                placeholder="https://apps.apple.com/app/..."
                helperText="For future iOS app release"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Maintenance Mode */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Warning color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Maintenance Mode</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch 
                    checked={maintenanceMode} 
                    onChange={(e) => setMaintenanceMode(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Box>
                    <Typography>Enable Maintenance Mode</Typography>
                    <Typography variant="caption" color="textSecondary">
                      ⚠️ This will show maintenance screen to ALL users
                    </Typography>
                  </Box>
                }
              />
              
              <TextField
                fullWidth
                label="Maintenance Message"
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                sx={{ mt: 2 }}
                multiline
                rows={3}
                placeholder="We are currently performing maintenance. Please try again later."
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Force Logout */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: config?.forceLogout?.enabled ? '2px solid #f44336' : 'none' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Logout color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">Force Logout</Typography>
                {config?.forceLogout?.enabled && (
                  <Chip label="ACTIVE" color="error" size="small" sx={{ ml: 2 }} />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {config?.forceLogout?.enabled ? (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Force logout is currently ACTIVE</Typography>
                    <Typography variant="body2">
                      Reason: {config.forceLogout.reason || "No reason provided"}
                    </Typography>
                    <Typography variant="caption">
                      Triggered at: {config.forceLogout.triggeredAt ? moment(config.forceLogout.triggeredAt).format("DD MMM YYYY HH:mm") : "Unknown"}
                    </Typography>
                  </Alert>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={handleClearForceLogout}
                    fullWidth
                  >
                    Clear Force Logout
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Force logout will log out ALL users from the app and require them to login again.
                    Use this after major security updates or when session refresh is needed.
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Logout />}
                    onClick={() => setForceLogoutDialogOpen(true)}
                    fullWidth
                  >
                    Trigger Force Logout for All Users
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Current Status */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>Current Status</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h5" color="primary">{config?.minimumVersion || "-"}</Typography>
                  <Typography variant="caption">Min Version</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h5" color="success.main">{config?.latestVersion || "-"}</Typography>
                  <Typography variant="caption">Latest Version</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  {config?.forceUpdate ? (
                    <CheckCircle color="warning" sx={{ fontSize: 32 }} />
                  ) : (
                    <Cancel color="disabled" sx={{ fontSize: 32 }} />
                  )}
                  <Typography variant="caption" display="block">Force Update</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box textAlign="center">
                  {config?.maintenanceMode ? (
                    <Warning color="error" sx={{ fontSize: 32 }} />
                  ) : (
                    <CheckCircle color="success" sx={{ fontSize: 32 }} />
                  )}
                  <Typography variant="caption" display="block">
                    {config?.maintenanceMode ? "Under Maintenance" : "App Running"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSaveConfig}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </Box>

      {/* Force Logout Dialog */}
      <Dialog open={forceLogoutDialogOpen} onClose={() => setForceLogoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ffebee' }}>
          ⚠️ Trigger Force Logout
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            This will force ALL users to logout from the app immediately.
            They will need to login again to use the app.
          </Alert>
          <TextField
            fullWidth
            label="Reason for Force Logout *"
            value={forceLogoutReason}
            onChange={(e) => setForceLogoutReason(e.target.value)}
            placeholder="e.g., Security update, Token refresh required"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceLogoutDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleTriggerForceLogout}
            disabled={triggeringLogout || !forceLogoutReason}
            startIcon={triggeringLogout ? <CircularProgress size={20} /> : <Logout />}
          >
            {triggeringLogout ? "Processing..." : "Confirm Force Logout"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppConfigPage;
